import StakeInitialization from "./staking";
import { fromSatoshi, toSatoshi } from "../lib/utils";
import OrangeKitTbtcRedeemerProxy from "../lib/redeemer-proxy";
/**
 * Module exposing features related to the account.
 */
export default class Account {
    /**
     * Acre contracts.
     */
    #contracts;
    /**
     * tBTC Module.
     */
    #tbtc;
    /**
     * Acre subgraph api.
     */
    #acreSubgraphApi;
    #bitcoinAddress;
    #ethereumAddress;
    #bitcoinPublicKey;
    #bitcoinProvider;
    #orangeKitSdk;
    constructor(contracts, tbtc, acreSubgraphApi, account, bitcoinProvider, orangeKitSdk) {
        this.#contracts = contracts;
        this.#tbtc = tbtc;
        this.#acreSubgraphApi = acreSubgraphApi;
        this.#bitcoinAddress = account.bitcoinAddress;
        this.#ethereumAddress = account.ethereumAddress;
        this.#bitcoinProvider = bitcoinProvider;
        this.#orangeKitSdk = orangeKitSdk;
        this.#bitcoinPublicKey = account.bitcoinPublicKey;
    }
    /**
     * Initializes the Acre deposit process.
     * @param referral Data used for referral program.
     * @param bitcoinRecoveryAddress `P2PKH` or `P2WPKH` Bitcoin address that can
     *        be used for emergency recovery of the deposited funds. If
     *        `undefined` the bitcoin address from bitcoin provider is used as
     *        bitcoin recovery address - note that an address returned by bitcoin
     *        provider must then be `P2WPKH` or `P2PKH`. This property is
     *        available to let the consumer use `P2SH-P2WPKH` as the deposit owner
     *        and another tBTC-supported type (`P2WPKH`, `P2PKH`) address as the
     *        tBTC Bridge recovery address.
     * @returns Object represents the deposit process.
     */
    async initializeStake(referral, bitcoinRecoveryAddress) {
        // tBTC-v2 SDK will handle Bitcoin address validation and throw an error if
        // address is not supported.
        const finalBitcoinRecoveryAddress = bitcoinRecoveryAddress ?? this.#bitcoinAddress;
        const tbtcDeposit = await this.#tbtc.initiateDeposit(this.#ethereumAddress, finalBitcoinRecoveryAddress, referral, this.#bitcoinAddress);
        return new StakeInitialization(tbtcDeposit);
    }
    /**
     * @returns Balance of the account's stBTC shares (in 1e18 precision).
     */
    async sharesBalance() {
        return this.#contracts.stBTC.balanceOf(this.#ethereumAddress);
    }
    /**
     * @returns Balance of Bitcoin position in Acre estimated based on the
     *          account's stBTC shares (in 1e8 satoshi precision).
     */
    async estimatedBitcoinBalance() {
        return toSatoshi(await this.#contracts.stBTC.assetsBalanceOf(this.#ethereumAddress));
    }
    /**
     * @returns All deposits associated with the account. They include all
     *          deposits: queued, initialized and finalized.
     */
    async getDeposits() {
        const subgraphData = await this.#acreSubgraphApi.getDepositsByOwner(this.#ethereumAddress);
        const initializedOrFinalizedDepositsMap = new Map(subgraphData.map((data) => [data.depositKey, data]));
        const tbtcData = await this.#tbtc.getDepositsByOwner(this.#ethereumAddress);
        return tbtcData.map((deposit) => {
            const depositFromSubgraph = initializedOrFinalizedDepositsMap.get(deposit.depositKey);
            const amount = toSatoshi(depositFromSubgraph?.amountToDeposit || deposit.initialAmount);
            return {
                id: deposit.depositKey,
                txHash: deposit.txHash,
                amount,
                status: deposit.status,
                initializedAt: deposit.initializedAt,
                finalizedAt: depositFromSubgraph?.finalizedAt,
            };
        });
    }
    /**
     * Initializes the withdrawal process.
     * @param amount Bitcoin amount to withdraw in 1e8 satoshi precision.
     * @param dataBuiltStepCallback A callback triggered after the data
     *        building step.
     * @param onSignMessageStepCallback A callback triggered before the message
     *        signing step.
     * @param messageSignedStepCallback A callback triggered after the message
     *        signing step.
     * @returns Hash of the withdrawal transaction and the redemption key.
     */
    async initializeWithdrawal(btcAmount, dataBuiltStepCallback, onSignMessageStepCallback, messageSignedStepCallback) {
        const tbtcAmount = fromSatoshi(btcAmount);
        const shares = await this.#contracts.stBTC.convertToShares(tbtcAmount);
        // Including fees.
        const redeemedTbtc = await this.#contracts.stBTC.previewRedeem(shares);
        const redeemerProxy = new OrangeKitTbtcRedeemerProxy(this.#contracts, this.#orangeKitSdk, {
            bitcoinAddress: this.#bitcoinAddress,
            ethereumAddress: this.#ethereumAddress,
            publicKey: this.#bitcoinPublicKey,
        }, this.#bitcoinProvider, shares, dataBuiltStepCallback, onSignMessageStepCallback, messageSignedStepCallback);
        return this.#tbtc.initiateRedemption(this.#bitcoinAddress, redeemedTbtc, redeemerProxy);
    }
    /**
     * @returns All withdrawals associated with the account.
     */
    async getWithdrawals() {
        return (await this.#acreSubgraphApi.getWithdrawalsByOwner(this.#ethereumAddress)).map((withdraw) => ({
            ...withdraw,
            amount: toSatoshi(withdraw.amount),
            status: withdraw.bitcoinTransactionId ? "finalized" : "initialized",
        }));
    }
}
//# sourceMappingURL=account.js.map